import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Award, Zap, Users, Phone, ArrowRight, Droplets, Wrench, Layers, Anchor, Bird, AlertCircle,
  Building2, Hotel, ShoppingBag, Factory, CheckCircle, Clock, MapPin, Timer, Ruler, Home as HomeIcon,
} from 'lucide-react';
import { BgImage, Img } from '../components/Img';
import { IMG } from '../images';
import { FAQS } from '../faqs';
import { AREAS } from '../areas';

const heroBadges = [
  { icon: <Award size={18} />, text: 'IRATA Level 3 certified technicians' },
  { icon: <Shield size={18} />, text: 'Fully insured' },
  { icon: <Clock size={18} />, text: '24/7 emergency response' },
  { icon: <MapPin size={18} />, text: 'Sydney & all of NSW' },
];

const stats = [
  { n: '10+', label: 'Years Experience', sub: 'Serving Sydney & NSW' },
  { n: '300+', label: 'Projects Completed', sub: 'Trusted by leading companies' },
  { n: '100%', label: 'Safety Record', sub: 'IRATA Level 3 certified' },
  { n: '24/7', label: 'Emergency Response', sub: 'Call 0426 920 301' },
];

const services = [
  {
    icon: <Droplets size={22} />,
    title: 'High Rise Window & Facade Cleaning',
    description: 'Internal & external window cleaning, facade washing for all materials, post-construction cleans.',
    link: '/services/window-cleaning',
    image: IMG.bucket,
    alt: IMG.bucket.alt,
  },
  {
    icon: <Wrench size={22} />,
    title: 'Remedial Work & Building Maintenance',
    description: 'Concrete repairs, caulking, sealing, protective coatings and leak investigation at height.',
    link: '/services/remedial',
    image: IMG.wash,
    alt: IMG.wash.alt,
  },
  {
    icon: <Layers size={22} />,
    title: 'Glazing & Panel Installation',
    description: 'Installation and replacement of glass panels, curtain wall repairs and facade panels.',
    link: '/services/glazing',
    image: IMG.lift,
    alt: IMG.lift.alt,
  },
  {
    icon: <Anchor size={22} />,
    title: 'Height Safety Installation',
    description: 'Installation of anchor points, certification and inspection, and height safety audits.',
    link: '/services/height-safety',
    image: IMG.ledge,
    alt: IMG.ledge.alt,
  },
  {
    icon: <Bird size={22} />,
    title: 'Bird Pest Control',
    description: 'Bird proofing systems, netting and spikes, plus cleaning and disinfection of affected areas.',
    link: '/services/bird-control',
    image: IMG.cityview,
    alt: IMG.cityview.alt,
  },
  {
    icon: <AlertCircle size={22} />,
    title: 'Emergency Rope Access',
    description: 'Emergency securing, urgent repairs, storm damage response and rapid assessment.',
    link: '/services/emergency',
    image: IMG.darkfacade,
    alt: IMG.darkfacade.alt,
  },
];

const industries = [
  { icon: <Building2 size={28} />, title: 'Commercial Buildings' },
  { icon: <Building2 size={28} />, title: 'Residential Strata' },
  { icon: <Factory size={28} />, title: 'Construction Companies' },
  { icon: <Hotel size={28} />, title: 'Hotels' },
  { icon: <ShoppingBag size={28} />, title: 'Shopping Centres' },
  { icon: <Building2 size={28} />, title: 'Office Towers' },
];

const whyRope = [
  { icon: <Timer size={30} />, title: 'Faster setup', description: 'No scaffolding or swing stages to build, so work starts sooner and finishes faster.' },
  { icon: <Users size={30} />, title: 'Less disruption', description: 'Minimal impact on tenants, residents and day-to-day operations.' },
  { icon: <Ruler size={30} />, title: 'Reaches anywhere', description: 'Curved facades, podiums, atriums and tight sites that other access methods cannot reach.' },
  { icon: <Zap size={30} />, title: 'Cost effective', description: 'Reduced equipment, labour and downtime compared with traditional access.' },
];

const steps = [
  { step: '01', title: 'Site Assessment', description: 'We review your building, access points and specific challenges.' },
  { step: '02', title: 'Scope & Quote', description: 'We define the work, methods and safety measures, then provide a clear quote.' },
  { step: '03', title: 'Safe Setup', description: 'Our team installs rope systems and safety equipment according to plan.' },
  { step: '04', title: 'Execution', description: 'We complete the work efficiently, with minimal disruption to occupants.' },
  { step: '05', title: 'Reporting', description: 'You receive a summary of work completed, with photos and recommendations.' },
];

const gallery = [IMG.lift, IMG.wavy, IMG.glassroof, IMG.whitefacade].map((image) => ({ image, tag: image.tag, alt: image.alt }));

const Home = () => (
  <div>
    {/* Hero */}
    <section className="hero">
      <BgImage image={IMG.hero} position="60% 50%" priority />
      <div className="hero-content">
        <span className="hero-eyebrow">Rope access specialists · Sydney &amp; NSW</span>
        <h1>High-Risk, High-Complexity Rope Access Solutions Across NSW</h1>
        <p>Fast, efficient and fully certified rope access services for high-rise buildings, complex structures and emergencies.</p>
        <div className="hero-buttons">
          <Link to="/contact#quote-form" className="btn-primary">Request a Quote <ArrowRight size={18} aria-hidden="true" /></Link>
          <a href="tel:0426920301" className="btn-secondary">
            <Phone size={18} aria-hidden="true" /> Emergency Call – Fast Response
          </a>
        </div>
        <ul className="hero-badges">
          {heroBadges.map((b) => (
            <li key={b.text}>{b.icon}{b.text}</li>
          ))}
        </ul>
      </div>
    </section>

    {/* Stats */}
    <section className="stats-strip" aria-label="ProClimb Solutions at a glance">
      <div className="stats-grid">
        {stats.map((s, i) => (
          <div key={s.label} className={`stat reveal d${i % 4}`}>
            <div className="stat-number">{s.n}</div>
            <div className="stat-label">{s.label}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>
    </section>

    {/* Intro split */}
    <section className="section">
      <div className="split">
        <div className="reveal">
          <span className="eyebrow">Who we are</span>
          <h2>When the Job Is Difficult, Dangerous or Time-Critical – We Step In</h2>
          <p>
            ProClimb Solutions is a specialised rope access team based in Sydney, delivering safe, efficient and high-quality
            work at height. Our technicians bring over 10 years of hands-on experience in industrial abseiling, high-rise
            maintenance and complex access solutions.
          </p>
          <ul className="check-list">
            <li><CheckCircle size={20} aria-hidden="true" /> Full-service solutions: cleaning, remedial, glazing and height safety</li>
            <li><CheckCircle size={20} aria-hidden="true" /> Safe execution on high-risk and high-complexity structures</li>
            <li><CheckCircle size={20} aria-hidden="true" /> Minimised disruption and cost through efficient rope-based access</li>
            <li><CheckCircle size={20} aria-hidden="true" /> Rapid response for emergency works and critical situations</li>
          </ul>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link to="/about" className="btn-outline">About ProClimb <ArrowRight size={18} aria-hidden="true" /></Link>
            <Link to="/premium" className="home-owner-card">
              <span className="home-owner-icon" aria-hidden="true"><HomeIcon size={22} /></span>
              <span className="home-owner-text">
                <strong>Private home owners</strong>
                <small>Luxury homes &amp; high-end properties</small>
              </span>
              <ArrowRight size={20} className="home-owner-arrow" aria-hidden="true" />
            </Link>
          </div>
        </div>
        <div className="photo-frame reveal d1">
          <Img image={IMG.team} alt="ProClimb Solutions technician in company uniform on site" sizes="(max-width: 900px) 100vw, 560px" style={{ aspectRatio: '2 / 3', maxHeight: '640px' }} />
        </div>
      </div>
    </section>

    {/* Services */}
    <section className="section bg-light">
      <div className="container">
        <div className="section-header reveal">
          <span className="eyebrow">What we do</span>
          <h2>Complete Rope Access Services for High-Rise and Complex Structures</h2>
          <p>
            A full range of rope access services designed to keep your building clean, safe and compliant, without the need for
            bulky scaffolding or expensive access equipment.
          </p>
        </div>
        <div className="svc-grid">
          {services.map((service, index) => (
            <Link key={service.title} to={service.link} className={`svc-card reveal d${index % 3}`}>
              <div className="svc-media">
                <Img image={service.image} alt={service.alt} sizes="(max-width: 768px) 100vw, 420px" />
                <span className="svc-icon" aria-hidden="true">{service.icon}</span>
              </div>
              <div className="svc-body">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <span className="link-arrow">Learn more <ArrowRight size={16} aria-hidden="true" /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* Why rope access */}
    <section className="section">
      <div className="container">
        <div className="section-header reveal">
          <span className="eyebrow">Why rope access</span>
          <h2>A Smarter Alternative to Scaffolding</h2>
          <p>Our rope access methods reach areas that are difficult, expensive or unsafe to access with traditional methods, while keeping disruption to a minimum.</p>
        </div>
        <div className="feature-grid">
          {whyRope.map((item, i) => (
            <div key={item.title} className={`feature reveal d${i % 4}`}>
              {item.icon}
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Process */}
    <section className="section bg-light">
      <div className="container">
        <div className="section-header reveal">
          <span className="eyebrow">How we work</span>
          <h2>A Clear, Safe Process From First Call to Final Report</h2>
        </div>
        <div className="steps">
          {steps.map((s, i) => (
            <div key={s.step} className={`step reveal d${i % 4}`}>
              <div className="step-number">{s.step}</div>
              <h3>{s.title}</h3>
              <p>{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Trust */}
    <section className="why-choose section">
      <div className="container">
        <div className="section-header reveal">
          <h2>Why Choose ProClimb Solutions</h2>
        </div>
        <div className="why-grid">
          {[
            { icon: <Award size={44} />, title: 'IRATA Level 3', description: 'Certified technicians and rigorous safety protocols on every job' },
            { icon: <Shield size={44} />, title: 'Fully Insured', description: 'Comprehensive insurance cover and thorough risk assessments' },
            { icon: <Zap size={44} />, title: 'Efficient', description: 'Quick deployment, expert execution and faster project completion' },
            { icon: <Users size={44} />, title: 'Skilled Team', description: '10+ years of experience from a multi-skilled team' },
          ].map((item, i) => (
            <div key={item.title} className={`why-item reveal d${i % 4}`}>
              <div style={{ color: 'var(--white)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Industries */}
    <section className="section">
      <div className="container">
        <div className="section-header reveal">
          <span className="eyebrow">Who we work with</span>
          <h2>Trusted by Strata, Commercial, Industrial and Construction Clients</h2>
        </div>
        <div className="industries-grid">
          {industries.map((industry, i) => (
            <div key={industry.title} className={`industry-card reveal d${i % 3}`}>
              <div className="industry-icon">{industry.icon}</div>
              <h3>{industry.title}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Gallery */}
    <section className="section bg-light">
      <div className="container">
        <div className="section-header reveal">
          <span className="eyebrow">From the field</span>
          <h2>Our Team at Work</h2>
          <p>Real jobs, real conditions: a few moments from recent rope access work across Sydney.</p>
        </div>
        <div className="gallery">
          {gallery.map((g) => (
            <figure key={g.alt} className="gallery-item reveal">
              <Img image={g.image} alt={g.alt} sizes="(max-width: 768px) 50vw, 33vw" />
              <figcaption><span className="gallery-tag">{g.tag}</span></figcaption>
            </figure>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <Link to="/projects" className="btn-outline">See the full gallery <ArrowRight size={18} aria-hidden="true" /></Link>
        </div>
      </div>
    </section>

    {/* Luxury homes */}
    <section className="banner">
      <BgImage image={IMG.premiumHero} />
      <div className="container reveal">
        <span className="pill">LUXURY HOMES</span>
        <h2>Rope Access for Luxury Homes and High-End Properties</h2>
        <p>
          Specialised rope access solutions for luxury residences, waterfront homes and architect-designed properties across
          Sydney. Discreet, professional service for high-value estates in Mosman, Vaucluse, Point Piper and Palm Beach.
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/premium" className="btn-primary">View Luxury Home Services</Link>
          <Link to="/contact#quote-form" className="btn-secondary">Request a Luxury Home Quote</Link>
        </div>
      </div>
    </section>

    {/* Areas */}
    <section className="section bg-light">
      <div className="container">
        <div className="section-header reveal">
          <span className="eyebrow">Where we work</span>
          <h2>Rope Access Across Sydney &amp; NSW</h2>
          <p>Based in Eastlakes, Sydney, and working across the city, Newcastle, Wollongong and surrounding regions.</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
          {AREAS.map((a) => (
            <Link key={a.slug} to={`/areas/${a.slug}`} className="mini-link" style={{ minWidth: '220px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><MapPin size={18} style={{ color: 'var(--safety-orange)' }} aria-hidden="true" /> {a.name}</span>
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="section">
      <div className="container">
        <div className="section-header reveal">
          <span className="eyebrow">FAQ</span>
          <h2>Frequently Asked Questions</h2>
        </div>
        <div className="faq reveal">
          {FAQS.map((item) => (
            <details key={item.q}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    {/* Final CTA */}
    <section className="final-cta">
      <BgImage image={IMG.facade} />
      <h2>Need Fast, Reliable Rope Access Solutions?</h2>
      <p style={{ fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto 2rem' }}>
        Whether you need a full facade clean, remedial repairs, glazing works or an urgent emergency response, ProClimb
        Solutions is ready to help.
      </p>
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link to="/contact#quote-form" className="btn-primary">Request a Quote</Link>
        <a href="tel:0426920301" className="btn-secondary">Emergency Call – Fast Response</a>
      </div>
    </section>
  </div>
);

export default Home;
